import { useState, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Modal, Select, adminMutate, useToast, type SelectOption } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { navigate } from '../router.js'
import { useGestes } from './associations-api.js'
import {
  LIBELLES_KIND, LIBELLES_STATUT, cheminFiche, signalerAssociations,
  type Association, type KindAssociation, type LigneAssociation, type ResultatGeste, type StatutAssociation,
} from './associations-model.js'

/**
 * EXPLOITATION › ASSOCIATIONS — les pièces partagées par la liste, la fiche
 * et « Comparer » : les états de lecture, les pastilles, le choix d'une
 * association et la création d'une association.
 */

export function Chargement({ libelle }: { libelle: string }) {
  return <p className="lc-hint asso-chargement" aria-busy="true">{libelle}…</p>
}

export function Erreur({ message, onReessayer }: { message: string; onReessayer?: () => void }) {
  return (
    <div className="asso-erreur" role="alert">
      <AlertTriangle size={16} aria-hidden="true" />
      <span>{message}</span>
      {onReessayer ? (
        <button type="button" className="lc-btn lc-btn--sm lc-btn--ghost" onClick={onReessayer}>
          <RefreshCw size={14} aria-hidden="true" /> Réessayer
        </button>
      ) : null}
    </div>
  )
}

export function PastilleKind({ kind }: { kind: KindAssociation }) {
  return <span className={`lc-pill asso-pill asso-pill--${kind.toLowerCase()}`}>{LIBELLES_KIND[kind]}</span>
}

export function PastilleStatut({ statut }: { statut: StatutAssociation }) {
  if (statut === 'ACTIVE') return null
  const ton = statut === 'SUGGESTED' ? 'lc-pill--warning' : 'lc-pill--idle'
  return <span className={`lc-pill ${ton}`}>{LIBELLES_STATUT[statut]}</span>
}

/** Un bloc titré de la page (lc-panel), avec une ancre facultative. */
export function Bloc(props: { id: string; titre: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`lc-panel gd-panel asso-bloc${props.className ? ` ${props.className}` : ''}`} aria-labelledby={`${props.id}-titre`} id={props.id}>
      <header className="lc-panel__head asso-bloc__tete">
        <h2 className="lc-section-title" id={`${props.id}-titre`}>{props.titre}</h2>
        {props.actions ? <div className="asso-bloc__actions">{props.actions}</div> : null}
      </header>
      <div className="lc-panel__body">{props.children}</div>
    </section>
  )
}

/** Le choix d'une association active (lier une soirée, marquer une nuit, fusionner). */
export function ChoixAssociation(props: {
  associations: ReadonlyArray<Pick<Association, 'id' | 'name' | 'kind' | 'status'>>
  value: string
  onChange: (id: string) => void
  label?: string
  sauf?: string
  /** La proposition de Gestion, mise en tête et nommée comme telle. */
  proposee?: string | null
}) {
  const actives = props.associations.filter((a) => a.status === 'ACTIVE' && a.id !== props.sauf)
    .sort((a, b) => (a.id === props.proposee ? -1 : b.id === props.proposee ? 1 : a.name.localeCompare(b.name, 'fr')))
  const options: SelectOption[] = actives.map((a) => ({
    value: a.id, label: `${a.name} · ${LIBELLES_KIND[a.kind]}${a.id === props.proposee ? ' — proposée par Gestion' : ''}`,
  }))
  return (
    <Select label={props.label} value={props.value} options={options} onChange={props.onChange}
      placeholder={actives.length === 0 ? 'Aucune association active' : 'Choisir une association'} ariaLabel={props.label ?? 'Association'} />
  )
}

const KINDS: Array<{ value: KindAssociation; label: string }> = [
  { value: 'BDE', label: 'BDE (bureau des élèves ou des étudiants)' },
  { value: 'ASSO', label: 'Association' },
  { value: 'GROUPEMENT', label: 'Groupement (Intersanté, Interfac…)' },
]
export const OPTIONS_KIND: SelectOption[] = KINDS

/** Les groupements possibles pour « membre de » (actifs ou suggérés). */
export function optionsGroupements(assos: ReadonlyArray<Pick<Association, 'id' | 'name' | 'kind' | 'status'>>, sauf?: string): SelectOption[] {
  return [
    { value: '', label: 'Aucun' },
    ...assos.filter((a) => a.kind === 'GROUPEMENT' && a.id !== sauf && (a.status === 'ACTIVE' || a.status === 'SUGGESTED'))
      .map((a) => ({ value: a.id, label: a.name })),
  ]
}

/**
 * CRÉER une association : confirmée d'emblée (c'est l'exploitant qui la
 * crée), avec ses alias — les orthographes qui la reconnaissent dans le nom
 * des soirées. La passe suivante lui lie ce qu'elle reconnaît, selon le
 * réglage de reconnaissance.
 */
export function ModaleNouvelle(props: {
  open: boolean
  onClose: () => void
  associations: readonly LigneAssociation[]
  /** Après la création : par défaut, ouvrir sa fiche. */
  onCree?: (id: string) => void
}) {
  const toast = useToast()
  const { annuler } = useGestes()
  const [nom, setNom] = useState('')
  const [kind, setKind] = useState<KindAssociation>('BDE')
  const [ecole, setEcole] = useState('')
  const [groupe, setGroupe] = useState('')
  const [alias, setAlias] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const fermer = () => { setErreur(null); props.onClose() }
  const creer = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const liste = alias.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean).slice(0, 20)
      const r = await adminMutate<ResultatGeste & { error?: string }>('POST', '/api/v1/associations', {
        // Un groupement n'appartient à aucun groupement : le choix fait avant de
        // passer le type à « Groupement » (champ masqué) n'est pas envoyé.
        name: nom.trim(), kind, school: ecole.trim() || null, member_of_id: kind === 'GROUPEMENT' ? null : groupe || null, ...(liste.length ? { alias: liste } : {}),
      })
      if (r.status !== 200 || !r.data.ok || !('association_id' in r.data)) { setErreur(r.data.message ?? 'Création impossible.'); return }
      // La création est un geste comme les autres : « ANNULER » l'archive (rien n'est supprimé).
      const geste = r.data.geste
      toast('ok', r.data.message, { action: { label: 'ANNULER', onAction: () => { void annuler(geste) } }, durationMs: 9000 })
      signalerAssociations()
      setNom(''); setEcole(''); setGroupe(''); setAlias('')
      props.onClose()
      const id = r.data.association_id
      if (id) (props.onCree ?? ((x: string) => navigate(cheminFiche(x))))(id)
    } finally { setEnvoi(false) }
  }
  return (
    <Modal open={props.open} onClose={fermer} ariaLabel="Nouvelle association" width={520}>
      <div className="asso-dialogue">
        <h3>Nouvelle association</h3>
        <Field label="Nom" helper="Le nom que l’équipe utilise : « Médecine », « ESN »…">
          <TextInput value={nom} onChange={setNom} maxLength={80} autoFocus ariaLabel="Nom de l’association" />
        </Field>
        <Select label="Type" value={kind} options={OPTIONS_KIND} onChange={(v) => setKind(v as KindAssociation)} />
        <Field label="École (facultatif)" helper="En toutes lettres : « Faculté de médecine de Rennes ».">
          <TextInput value={ecole} onChange={setEcole} maxLength={160} ariaLabel="École" />
        </Field>
        {kind !== 'GROUPEMENT' ? (
          <Select label="Membre d’un groupement" value={groupe} options={optionsGroupements(props.associations)} onChange={setGroupe} />
        ) : null}
        <Field label="Alias (facultatif)" helper="Les mots du nom des soirées qui la désignent, séparés par des virgules : « MEDECINE, MEDOC ». Jamais « étudiant », « campus » ni « soirée ».">
          <TextInput value={alias} onChange={setAlias} maxLength={600} ariaLabel="Alias" />
        </Field>
        {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
        <div className="asso-dialogue__pied">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || !nom.trim()} onClick={() => { void creer() }}>
            {envoi ? 'Création…' : 'Créer l’association'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
